import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { EndpointDef } from "@/features/users/data/endpoints";
import { permissionGroupKeys } from "@/features/users/hooks/usePermissionGroups";
import {
  createApiPermission,
  deleteApiPermission,
  listApiPermissions,
  updateApiPermission,
  type CreateApiPermissionInput,
  type UpdateApiPermissionInput,
} from "@/features/users/services/apiPermissionCatalog";

export const apiPermissionKeys = {
  all: ["apiPermissions"] as const,
  list: () => [...apiPermissionKeys.all, "list"] as const,
};

/**
 * Catálogo de permisos desde Firestore (`api_permissions`).
 */
export function useEndpointsConfig() {
  return useQuery<EndpointDef[]>({
    queryKey: apiPermissionKeys.list(),
    queryFn: listApiPermissions,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateApiPermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateApiPermissionInput) => createApiPermission(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: apiPermissionKeys.all });
    },
  });
}

export function useUpdateApiPermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      docId,
      data,
    }: {
      docId: string;
      data: UpdateApiPermissionInput;
    }) => updateApiPermission(docId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: apiPermissionKeys.all });
    },
  });
}

export function useDeleteApiPermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (docId: string) => deleteApiPermission(docId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: apiPermissionKeys.all });
      // deleteApiPermission limpia permissionIds en grupos
      await queryClient.invalidateQueries({ queryKey: permissionGroupKeys.all });
    },
  });
}
